<script setup>
import { computed, ref, watch, toRefs } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';

import AppTransition from '@/components/general/Transition.vue';
import AppCreditsTag from '@/components/billing/credits/CreditsTag.vue';

import { useProjectStore } from '@/stores/project';

import { useData } from '@/composables/useData';

const emit = defineEmits(['closeSidebar']);

const projectStore = useProjectStore();
const router = useRouter();
const route = useRoute();
const projectId = computed(() => route.params.projectId);

const projects = computed(() =>
  projectStore.projects.map((v) => ({
    name: v.project.name,
    code: v.project.id,
    role: v.role,
    credit_balance: v.project.credit_balance,
  }))
);

const loading = computed(
  () => projectStore.projectsLoading && !projects.value.length
);

const selectedProject = computed(() => {
  return projects.value.find(
    (project) => project.code == projectStore.currentProjectId
  );
});

// Update query param when selected project changes
watch(selectedProject, (newVal, oldVal) => {
  // Open project list dropdown if selectedProject is undefined
  if (!newVal) {
    showProjectList.value = true;
  }
  if (newVal && oldVal) {
    if (newVal.code !== oldVal.code && oldVal.code === projectId.value) {
      applyProject(newVal.code);
    }
  }
});
function onSelectedProjectChange(project) {
  if (project) {
    applyProject(project.code);
  }
}
function applyProject(projectId) {
  projectStore.setCurrentProjectById(projectId);
  emit('closeSidebar');
  const refreshOnLanding = route.name === 'Dashboard';
  router.push({ name: 'Dashboard', params: { projectId } }).then(() => {
    if (refreshOnLanding) window.location.reload();
  });
}

const { memberRoles: roleMap } = useData();
function getRole(role) {
  return roleMap[role] || roleMap['READONLY'];
}

const showProjectList = ref(false);

// Open project list dropdown if no project is selected
if (!selectedProject.value) {
  showProjectList.value = true;
}

function closeProjectList() {
  if (!selectedProject.value) return;
  setTimeout(() => {
    showProjectList.value = false;
  }, 0);
}

defineExpose({
  showProjectList,
});
</script>

<template>
  <div class="relative w-full px-0 pb-2 pt-2">
    <div class="">
      <div
        @click="selectedProject && (showProjectList = !showProjectList)"
        v-on-click-outside="closeProjectList"
        class="mb-1 flex w-full cursor-pointer justify-between truncate py-2 pl-5 pr-3 hover:bg-surface-100 hover:text-surface-700 dark:bg-surface-700 dark:hover:bg-surface-400/10 dark:hover:text-white/80"
        :class="{
          'border border-solid border-red-500': !selectedProject,
        }"
      >
        <span v-if="loading">Loading...</span>
        <span v-else-if="selectedProject" class="w-full whitespace-nowrap">
          <div>
            {{ selectedProject.name }}
          </div>
        </span>
        <span v-else class="text-red-500">Please select a project first</span>
        <div>
          <i
            v-if="loading"
            class="icon-[prime--spinner] -mb-1.5 h-6 w-6 animate-spin"
          />
          <i
            v-else
            class="icon-[prime--chevron-right] -mb-1.5 h-6 w-6"
            :class="{
              'text-red-500': !selectedProject,
            }"
          />
          <!-- <i class="icon-[prime--chevron-right] h-6 w-6" /> -->
        </div>
      </div>
      <div class="flex gap-2 px-5" v-if="selectedProject">
        <app-credits-tag />
        <Tag
          v-if="selectedProject.role"
          severity="secondary"
          class="cursor-default"
          >{{ getRole(selectedProject.role) }}</Tag
        >
      </div>
    </div>
    <app-transition>
      <Teleport to="body">
        <Listbox
          ref="projectList"
          v-if="!selectedProject || showProjectList"
          :modelValue="selectedProject"
          @update:modelValue="onSelectedProjectChange"
          optionLabel="name"
          :options="projects"
          class="sidebar-width !content-border-color fixed top-[10rem] z-[10000] overflow-hidden rounded-t-none pt-0 shadow-lg md:left-[21.5rem] md:top-[5rem] md:w-auto md:rounded-md"
          pt:list:class="py-0 max-h-[20rem]"
        >
          <template #footer>
            <router-link to="/projects" v-slot="{ href, navigate }">
              <a
                class="content-border-color flex justify-center border-t px-4 py-3 font-bold text-primary-500"
                v-ripple
                :href="href"
                @click="
                  emit('closeSidebar');
                  navigate();
                "
              >
                <span class="text-center">View all</span>
              </a>
            </router-link>
          </template>
        </Listbox>
      </Teleport>
    </app-transition>
  </div>
</template>
